import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { TrailerDetailsForm } from './TrailerDetailsForm';
import { TrailerHistoryTab } from './TrailerHistoryTab';
import { TrailerOwnershipTab } from './TrailerOwnershipTab';
import { TrailerStatusControl } from './TrailerStatusControl';
import { useTrailerDetail } from './use-trailer-detail';

/**
 * A trailer's own page: fields, ownership and history on their own tabs. `fleet.view` reads;
 * `truck.manage` mutates — every edit control here is hidden or disabled for a
 * `fleet.view`-only visitor, same as CustomerDetailPage. Pairing to trips and documents
 * are out of scope here — see gitaumoses4/chukua#13.
 */
export function TrailerDetailPage() {
  const { id = '' } = useParams();
  const { state, updateError, saving, update, activate, deactivate } = useTrailerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('truck.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading trailer" tabs={3} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Trailer not found" description="This trailer doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this trailer" description="Something went wrong loading this trailer. Try reloading the page." />
    );
  }

  const { trailer } = state;

  return (
    <>
      <PageHeader
        eyebrow="Assets"
        title={trailer.registration}
        description={trailer.typeCode}
        actions={<TrailerStatusControl trailer={trailer} canManage={canManage} onActivate={activate} onDeactivate={deactivate} />}
      />
      <Tabs
        items={[
          {
            key: 'details',
            label: 'Details',
            children: <TrailerDetailsForm trailer={trailer} canManage={canManage} error={updateError} saving={saving} onSave={update} />,
          },
          {
            key: 'ownership',
            label: 'Ownership',
            children: <TrailerOwnershipTab trailerId={trailer.id} trailerRegistration={trailer.registration} canManage={canManage} />,
          },
          { key: 'history', label: 'History', children: <TrailerHistoryTab trailerId={trailer.id} /> },
        ]}
      />
    </>
  );
}
